import axios from "../axios";

// Upload a single product
export const uploadProduct = async (data) => {
  try {
    const response = await axios.post("/products/upload", data);
    return response.data;
  } catch (error) {
    console.error("Error uploading product:", error);
    const message = error.response?.data?.error?.message || error.message || "Failed to upload product. Please try again.";
    throw new Error(message);
  }
};

// Bulk upload products (CSV)
export const bulkUploadProducts = async (formData) => {
  const response = await axios.post("/products/bulk-upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

// Get all products
export const getAllProducts = async () => {
  try {
    const response = await axios.get("/products");
    // New API format: { success: true, products: [...], total: number }
    if (response.data?.success && Array.isArray(response.data.products)) {
      return response.data.products;
    }
    // Fallback for backward compatibility
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      return response.data.data;
    } else if (response.data?.products && Array.isArray(response.data.products)) {
      return response.data.products;
    }
    console.warn("Unexpected response structure:", response.data);
    return [];
  } catch (error) {
    console.error("Failed to fetch products:", error);
    throw error;
  }
};

// Get all products for admin (with detailed info)
export const getAllProductsForAdmin = async () => {
  try {
    const response = await axios.get("/products/all");
    // Backend returns { products: [...], total: number }
    if (response.data?.products && Array.isArray(response.data.products)) {
      return response.data.products;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    console.warn("Unexpected response structure:", response.data);
    return [];
  } catch (error) {
    console.error("Failed to fetch products for admin:", error);
    const message = error.response?.data?.error?.message || "Failed to fetch products. Please try again.";
    throw new Error(message);
  }
};

// Get product by ID or productId
export const getProductById = async (productId) => {
  try {
    const { data } = await axios.get(`/products/${productId}`);
    // New API format: { success: true, product: {...}, variations: [...], reviews: [...] }
    if (data?.success) {
      return data;
    }
    // Fallback for backward compatibility
    return data;
  } catch (error) {
    console.error("Error fetching product:", error);
    throw error;
  }
};

// Update a product
export const updateProduct = async (productId, data) => {
  try {
    const response = await axios.put(`/products/${productId}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating product:", error);
    const message = error.response?.data?.error?.message || error.message || "Failed to update product. Please try again.";
    throw new Error(message);
  }
};

// Delete a product
export const deleteProduct = async (productId) => {
  try {
    const response = await axios.delete(`/products/${productId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting product:", error);
    const message = error.response?.data?.error?.message || error.message || "Failed to delete product. Please try again.";
    throw new Error(message);
  }
};

// Fetch product reviews
export const getProductReviews = async (productId) => {
  try {
    const response = await axios.get(`/products/${productId}/reviews`)
    return response.data.reviews
  } catch (error) {
    const message =
      error.response?.data?.error?.message || "Failed to fetch product reviews."
    throw new Error(message)
  }
}

// ✅ Get Best Sellers (with optional limit)
export const getBestSellers = async (limit = 6) => {
  try {
    const response = await axios.get(`/products/best-sellers?limit=${limit}`);
    // New API format: { success: true, products: [...], total: number }
    if (response.data?.success && Array.isArray(response.data.products)) {
      return response.data.products;
    }
    // Fallback for backward compatibility
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      return response.data.data;
    } else if (response.data?.products && Array.isArray(response.data.products)) {
      return response.data.products;
    }
    console.warn("Unexpected response structure:", response.data);
    return [];
  } catch (error) {
    console.error("Failed to fetch best sellers:", error);
    const message =
      error.response?.data?.error?.message || "Failed to fetch best sellers.";
    throw new Error(message);
  }
};

// Submit a review
export const submitReview = async (productId, data) => {
  try {
    const response = await axios.post(`/products/${productId}/reviews`, data)
    return response.data.review
  } catch (error) {
    const message =
      error.response?.data?.error?.message || "Failed to submit review. Please try again."
    throw new Error(message)
  }
}

export function normalizeProduct(product) {
  // Parse price and discount from string to number
  const price = Number(
    typeof product.price === "string"
      ? product.price.replace(/[^0-9.]/g, "")
      : product.price
  );
  
  const discount = Number(
    typeof product.discount === "string"
      ? product.discount.replace(/[^0-9.]/g, "")
      : product.discount || 0
  );

  // Handle images - check for valid non-empty strings
  const getValidImage = (img) => {
    return img && typeof img === "string" && img.trim() !== "" ? img : null;
  };

  const thumbnail = getValidImage(product.thumbnail);
  const image = getValidImage(product.image);
  const imagesArray = Array.isArray(product.images) && product.images.length > 0
    ? product.images.filter(img => getValidImage(img))
    : null;

  // Determine the primary image
  const primaryImage = thumbnail || image || (imagesArray && imagesArray[0]) || "/placeholder.svg";
  
  // Determine the images array
  const normalizedImages = imagesArray || (primaryImage !== "/placeholder.svg" ? [primaryImage] : []);

  // Normalize category - handle both category_name and category fields
  const category = product.category_name || product.category || null;

  return {
    ...product,
    image: primaryImage,
    images: normalizedImages,
    thumbnail: thumbnail || primaryImage,
    rating: product.average_rating || product.rating || 0,
    reviews: product.reviews_count || product.reviews || 0,
    price: price,
    discount: discount,
    category: category, // Normalized category field
    category_name: category, // Keep for backward compatibility
    category_slug: product.category_slug || null,
    subcategory: product.subcategory || null, // Subcategory field
    // Ensure productId is available
    productId: product.productId || product.id,
    id: product.id || product.productId,
  };
}